/**
 * The pane's "hands": everything it does to the open document (spec §5). Word implements this in
 * `hands/word.ts`; a later Google Docs implementation would implement the same interface (spec §0.1).
 *
 * Positions are always a quote within a paragraph id (pid), never character offsets (spec §3).
 */

import type { AnchorLevel } from "../anchor/anchor";
import type { WordSnapshot } from "../anchor/snapshot";

export type TrackingMode = "Off" | "TrackAll" | "TrackMineOnly";

/** Where a quote lives. `paragraphHash` is the snapshot's hash of that paragraph (confirms the pid still fits). */
export interface AnchorTarget {
  pid: string;
  quote: string;
  paragraphHash?: string;
}

export type AnchorStatus = "ok" | "not_found" | "ambiguous" | "pending_change" | "locked";

export interface AnchorOutcome {
  status: AnchorStatus;
  /** The paragraph actually used (may differ from the requested pid when the document moved). */
  pid: string | null;
  /** How the paragraph was found: the pid itself, a unique paragraph hash, or a unique whole-story search. */
  resolution: "pid" | "hash" | "search" | null;
  level: AnchorLevel | null;
  /** The document's own characters at the anchored span. */
  matchedText: string | null;
  detail: string;
}

/** A verified edit as the server published it (spec §4 `propose_edits`, verdict `ok`). */
export interface PaneEdit {
  id: string;
  pid: string;
  paragraphHash?: string;
  quote: string;
  replacement: string;
  op: "replace" | "delete" | "insert_after";
  why: string;
}

/**
 * Told when Ella is about to switch Track Changes on for a mutation (`begin`, with the user's mode) and when it
 * has been put back (`end`). A `begin` without an `end` means the pane died or the restore failed, so the next
 * pane load can offer to put the mode back (spec §5.2 "left off" banner).
 */
export interface ModeJournal {
  begin(previousMode: TrackingMode): void;
  end(): void;
}

export interface ApplyOptions {
  /** `tracked` = a tracked change under the user's name; `clean` only when Track Changes is already off. */
  mode: "tracked" | "clean";
  /** Put the reason in the Word comment (default: only "Suggested by Ella", spec D4). */
  includeReason?: boolean;
  journal?: ModeJournal;
}

export type ApplyStatus =
  | "applied"
  /**
   * The edit is in the document, but Word added a stray space next to it (Word on the web, footnotes; spike
   * P7) and Ella could not confirm it is gone. Everything `applied` carries is set; `detail` says what to check.
   */
  | "applied_unverified"
  | "noop"
  | "refused"
  | "not_found"
  | "ambiguous"
  | "pending_change"
  | "locked"
  /** Word will not change this document at all (protected or read-only): one message, not a card failure. */
  | "protected"
  | "error";

export interface ApplyResult {
  status: ApplyStatus;
  /** `diff` = word-level hunks; `whole` = the anchored span replaced in one go; `insert`/`delete` for those ops. */
  method?: "diff" | "whole" | "insert" | "delete";
  /** Number of separate changes made in Word. */
  changes?: number;
  commentId?: string;
  /**
   * Plain sentence for the user when not applied; for `applied_unverified`, what to check; for `applied`, a
   * note when the comment could not go where it belongs (e.g. on the whole paragraph after a delete).
   */
  detail: string;
  /** True when Track Changes could not be put back as it was (the pane must warn). */
  modeRestoreFailed?: boolean;
  /**
   * `applied` only. The paragraph the edit actually landed in (it may differ from the requested pid), that
   * paragraph's hash as Word reports it after the edit, and how many paragraphs its story gained (+) or lost (-).
   * Absent when Word could not be re-read after the edit.
   */
  pid?: string;
  paragraphHashAfter?: string;
  paragraphDelta?: number;
  /**
   * `applied` only: the edit is in the document but the Word comment could not be added. `retryComment` is
   * where to put it (the edited text), or absent when there is nothing to anchor it to.
   */
  commentError?: string;
  retryComment?: PaneComment;
  /** `applied` only: an insertion could not be placed word by word, so the whole quoted span was replaced. */
  imprecise?: boolean;
  /**
   * Stray spaces Word itself added next to the edit (Word on the web, footnotes; spike P2 B1, P7) that Ella
   * rejected again. `spacingRepairFailed`: the spacing could not be confirmed (status `applied_unverified`).
   */
  spacesRemoved?: number;
  spacingRepairFailed?: boolean;
  /**
   * Status `applied_unverified`: a note replace written as insert-Before + delete (Word on the web, spike P9 N4)
   * did not read back as exactly the planned tracked changes at the planned places; `detail` says to check it.
   */
  placementUnverified?: boolean;
  /**
   * The Word comment was put on this note's reference mark in the main text ("footnote 1"), not inside the
   * note: Word on the web drops comments inside footnotes/endnotes from the saved/exported file (spike P2 B3).
   */
  commentOnNote?: string;
}

export interface PaneComment {
  pid: string;
  paragraphHash?: string;
  quote: string;
  text: string;
}

export interface CommentResult {
  status: "added" | "not_found" | "ambiguous" | "locked" | "protected" | "error";
  commentId?: string;
  detail: string;
  /** As `ApplyResult.commentOnNote`. */
  commentOnNote?: string;
}

/** A place in a paragraph as captured: `offset` is a UTF-16 index into that paragraph's text (null: unknown). */
export interface CapturedSpot {
  pid: string;
  paragraphHash: string;
  offset: number | null;
}

/**
 * The selection (or cursor) as it was when Ella read the document. "Replace selection" and "Insert at cursor"
 * act on THIS, never on whatever is selected when the user clicks (a ¶ link or Show button moves the
 * selection). Kept in pane memory only; it is not part of the uploaded snapshot.
 */
export interface SelectionTarget {
  start: CapturedSpot;
  /** Where the selection ends; the cursor for "Insert at cursor". Same pid as `start` within one paragraph. */
  end: CapturedSpot;
  /** The selected text without a trailing paragraph mark; "" when only a cursor was placed. */
  text: string;
  /** Paragraphs in `start`'s story at capture: an index is only trusted while this is unchanged. */
  storyParagraphs: number;
}

export interface InsertRequest {
  at: "cursor" | "after_pid" | "replace_selection";
  pid?: string;
  paragraphHash?: string;
  /** Required for `cursor` / `replace_selection`: where the selection/cursor was when Ella read the document. */
  target?: SelectionTarget | null;
  markdown: string;
  mode: "tracked" | "clean";
  journal?: ModeJournal;
}

export interface InsertResult {
  status: "inserted" | "refused" | "not_found" | "locked" | "protected" | "error";
  paragraphs?: number;
  detail: string;
  modeRestoreFailed?: boolean;
}

export interface NavigateResult {
  status: "selected" | "not_found" | "ambiguous" | "error";
  detail: string;
}

export interface CaptureDiagnostics {
  /** Tracked changes / comments dropped because their paragraph could not be identified. */
  droppedTrackedChanges: number;
  droppedComments: number;
  /** Which optional parts were captured (requirement-set gated). */
  footnotes: boolean;
  trackedChanges: boolean;
  reviewedText: boolean;
  /**
   * Paragraphs whose `text_live` holds tracked-deleted text (Word on the web): placed exactly
   * (`live_segments` set) or not placed (left without segments; the tracked-change list still has them).
   */
  deletedTextPlaced: number;
  deletedTextUnplaced: number;
  syncs: number;
}

export interface CaptureResult {
  snapshot: WordSnapshot;
  diagnostics: CaptureDiagnostics;
  /** The selection/cursor at capture (null when it could not be tied to a paragraph). Pane memory only. */
  target: SelectionTarget | null;
}

export interface CaptureOptions {
  /** Server-minted id from `…/word/snapshots/init`; a random UUID when omitted (local/debug use). */
  snapshotId?: string;
}

export interface DocumentHands {
  capture(options?: CaptureOptions): Promise<CaptureResult>;
  anchor(target: AnchorTarget): Promise<AnchorOutcome>;
  applyEdit(edit: PaneEdit, options: ApplyOptions): Promise<ApplyResult>;
  addComment(comment: PaneComment): Promise<CommentResult>;
  insertText(request: InsertRequest): Promise<InsertResult>;
  navigate(pid: string, quote?: string): Promise<NavigateResult>;
  getTrackingMode(): Promise<TrackingMode>;
  setTrackingMode(mode: TrackingMode): Promise<void>;
  removeComments(ids: readonly string[]): Promise<{ removed: string[]; missing: string[] }>;
}

export class SnapshotTooLargeError extends Error {
  constructor(readonly chars: number) {
    super(`This document has ${chars.toLocaleString("en-US")} characters; Ella can read at most 1,000,000.`);
    this.name = "SnapshotTooLargeError";
  }
}

export class WordTooOldError extends Error {
  constructor(readonly needed: string) {
    super(`This version of Word is too old for Ella (needs Word API ${needed}).`);
    this.name = "WordTooOldError";
  }
}
